import { useCallback, useMemo, useState } from 'react'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { confirmAccount } from '@/account/accountApi'
import { AccountConfirmationFormValues } from './rules/accountConfirmationSchema'
import { AddressDetails } from '@/common/types'

interface UseAccountConfirmationLogicResult {
    data: AccountConfirmationFormValues | undefined
    loading: boolean
    saving: boolean
    isSuccess: boolean
    isConfirmationRequired: boolean
    error: string | null
    onSubmit: () => Promise<void>
}

export function useAccountConfirmationLogic(): UseAccountConfirmationLogicResult {
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const confirmAccountStatus = useAccountStore((s) => s.confirmAccountStatus)
    const [saving, setSaving] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const isConfirmationRequired = account?.confirmationStatus === 'REQUIRED'

    const data = useMemo(() => {
        if (!user) return undefined

        const defaultAddress: AddressDetails = {
            addressPrefix: '',
            streetNumber: '',
            streetName: '',
            streetType: '',
            city: '',
            state: '',
            postCode: '',
            country: '',
        }

        return {
            salutation: user.salutation || '',
            firstName: user.firstName || '',
            middleName: user.middleName || '',
            lastName: user.lastName || '',
            dateOfBirth: user.dateOfBirth || '',
            mobile: user.mobile || '',
            residential: (user.addresses?.residentialAddress || defaultAddress) as AddressDetails,
            postal: (user.addresses?.postalAddress || defaultAddress) as AddressDetails,
            adviser: account?.adviserName || '',
        }
    }, [user, account?.adviserName])

    const onSubmit = useCallback(async () => {
        if (!account?.accountId) {
            setError('Account ID is required to confirm account')
            return
        }

        setSaving(true)
        setError(null)
        try {
            await confirmAccount(account.accountId)
            confirmAccountStatus(account.accountId)
            setIsSuccess(true)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to confirm account'
            setError(errorMessage)
        } finally {
            setSaving(false)
        }
    }, [account?.accountId, confirmAccountStatus])

    return {
        data,
        loading: false,
        saving,
        isSuccess,
        isConfirmationRequired,
        error,
        onSubmit,
    }
}
